<template>
  <div>
    <div class="category-slider swiper-container" ref="container">
      <div class="swiper-wrapper">
        <div
          class="category-slider__slide swiper-slide"
          v-for="(category, index) in categories"
          :key="index"
          @slide-click="() => handleSlideClick(index)"
        >
          <IntersectionObserver
            @on-intersection="
              () =>
                $emit('on-intersection', {
                  title: category.title,
                  imageUrl: category.backgroundUrl,
                  type: 'category',
                  index,
                })
            "
          >
            <div class="category-slider__card">
              <CategoryCard
                class="category-slider__card-content"
                :ctaCopy="category.ctaCopy"
                :ctaUrl="category.ctaUrl"
                :title="category.title"
                :backgroundUrl="category.backgroundUrl"
                @on-click="category.onClick"
              />
            </div>
          </IntersectionObserver>
        </div>
      </div>
      <div
        class="category-slider__nav-button category-slider__nav-button--prev swiper-button-prev"
        ref="prev"
      >
        <IconForward class="category-slider__nav-icon category-slider__nav-icon--prev" />
      </div>
      <div
        class="category-slider__nav-button category-slider__nav-button--next swiper-button-next"
        ref="next"
      >
        <IconForward class="category-slider__nav-icon category-slider__nav-icon--next" />
      </div>
    </div>
  </div>
</template>

<script>
import Swiper, { Navigation } from 'swiper'
import { responsiveMixin } from 'on-ui'
import { BREAKPOINTS } from 'on-ui/utils/constants'
import IntersectionObserver from '@/components/_shared/IntersectionObserver.vue'
import IconForward from '@/components/_icons/IconForward.svg'
import CategoryCard from '@/components/Home/CategorySlider/CategoryCard.vue'

// Install swiper modules
Swiper.use([Navigation])

export default {
  name: 'CategorySlider',

  components: {
    IntersectionObserver,
    CategoryCard,
    IconForward,
  },

  mixins: [responsiveMixin],

  props: {
    categories: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      swiper: null,
    }
  },

  mounted() {
    this.initSwiper()

    window.addEventListener(
      'resize',
      On.Debouncer().debounce({
        debounceTime: 100,
        callback: this.resetSwiper,
      })
    )
  },

  beforeDestroy() {
    if (this.swiper) this.swiper.destroy()
  },

  methods: {
    initSwiper() {
      this.swiper = new Swiper(this.$refs.container, {
        navigation: {
          nextEl: this.$refs.next,
          prevEl: this.$refs.prev,
        },
        initialSlide: !this.$responsive_isTabletLarge ? 2 : 0,
        centeredSlides: !this.$responsive_isTabletLarge,
        simulateTouch: false,
        slidesPerView: 'auto',
        slidesPerGroup: 1,
        breakpoints: {
          [BREAKPOINTS.desktop]: {
            slidesPerGroup: 2,
          },
          [BREAKPOINTS.desktopLarge]: {
            slidesPerGroup: 3,
          },
        },
        threshold: 32,
      })
    },

    resetSwiper() {
      if (this.swiper) {
        this.swiper.destroy()
        this.swiper = null
        this.initSwiper()
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import '@/assets/stylesheets/base/animations.scss';
@import '@/assets/stylesheets/swiper.scss';

$icon-size: 10px;

.swiper-wrapper {
  * {
    box-sizing: border-box;
  }
}

.category-slider {
  width: 100vw;
  position: relative;
  left: calc(50% - 50vw);

  &__slide {
    width: calc(100% - 96px);
    overflow: hidden;
    // using borders instead of margin for IE11 support
    border: 8px solid transparent;
    border-width: 0 8px;

    @media ($tablet-up) {
      max-width: 360px;
      width: calc(100% - 256px);
    }

    @media ($tablet-large-up) {
      // using borders instead of margin for IE11 support
      border-width: 0 12px;
      min-width: 260px;
      width: 29%;
    }

    @media ($desktop-huge-up) {
      width: 21%;
    }

    @media ($tablet-large-up) {
      &:first-child {
        margin-left: 24px;
      }
    }
  }

  &__nav-button {
    width: 48px;
    height: 48px;
    background: $color-white;
    color: $color-grey-dark-2;
    transition: all 0.2s ease-out;
    display: none;

    @media ($tablet-large-up) {
      display: flex;
    }

    &:after {
      display: none;
    }

    &:hover {
      background: $c-grey-light-2;
    }

    &--prev {
      border-radius: 0 50% 50% 0;
      left: 0;
    }

    &--next {
      border-radius: 50% 0 0 50%;
      right: 0;
    }

    &.swiper-button-disabled {
      opacity: 0;
    }
  }

  &__nav-icon {
    width: $icon-size;
    height: $icon-size;
    display: block;
    margin: 0;

    &--prev {
      transform: rotate(180deg);
    }
  }
}
</style>
